import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDateShort, renderReminder } from '@topspin/shared';
import type { Session } from '@topspin/shared';
import { fetchReminder } from '../api.js';
import { today, type SeasonView } from '../data.js';
import { getPlayerId } from '../storage.js';
import { nextScheduledSession } from '../session-utils.js';

/**
 * Admin copy/paste view (§6.5). The text is rendered locally from the same shared renderer the
 * API and the reminder email use, so it works offline; the server copy is fetched when online
 * as a cross-check that nothing has drifted.
 *
 * One card per group, each showing that group's next match, ordered by date rather than by the
 * order the group IDs happen to be stored in. The two groups' nights alternate as the week
 * turns — Tuesday then Thursday until Tuesday is played, then Thursday and the following
 * Tuesday — and the cards are pasted in that order, so the page reads down in the order the
 * messages go out.
 */
export default function ReminderScreen({ view }: { view: SeasonView }) {
  const date = today(view);
  const [copied, setCopied] = useState<string | undefined>();
  // A group whose season has run out sorts last, after every group that still has tennis
  // left; its card is the one saying there is nothing to send.
  const sortKey = (session: Session | undefined) => session?.date ?? '9999-12-31';
  // Only the groups the admin ID covers — the server cross-check below is an admin-only read,
  // and a group reached through the players' ID has no business being drafted here (§5).
  const upcoming = view.groups
    .filter((group) => group.admin)
    .map((group) => ({ group, session: nextScheduledSession(group, date) }))
    .sort((a, b) => sortKey(a.session).localeCompare(sortKey(b.session)));

  return (
    <>
      <h1>Reminders</h1>
      {upcoming.map(({ group, session }) => {
        const local = session
          ? renderReminder(
              group.config,
              session,
              group.overrides,
              group.availabilities,
              group.fills,
            )
          : null;
        return (
          <section className="card" key={group.config.groupId}>
            <h2>{group.config.name}</h2>
            {!session || !local ? (
              <p className="muted">Nothing to send — no session coming up.</p>
            ) : (
              <>
                <p className="muted">{formatDateShort(session.date)}</p>
                <pre className="reminder">{local.body}</pre>
                <div className="actions">
                  <button
                    type="button"
                    className="primary"
                    onClick={async () => {
                      await navigator.clipboard.writeText(local.body);
                      setCopied(group.config.groupId);
                      setTimeout(() => setCopied(undefined), 2000);
                    }}
                  >
                    {copied === group.config.groupId ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <ServerCheck
                  groupKey={group.key}
                  seasonId={view.seasonId}
                  groupId={group.config.groupId}
                  date={session.date}
                  local={local.body}
                />
              </>
            )}
          </section>
        );
      })}
    </>
  );
}

function ServerCheck({
  groupKey,
  seasonId,
  groupId,
  date,
  local,
}: {
  groupKey: string;
  seasonId: string;
  groupId: string;
  date: string;
  local: string;
}) {
  const { data } = useQuery({
    queryKey: ['reminder', groupId, date],
    queryFn: () => fetchReminder(groupKey, getPlayerId(), { seasonId, groupId, date }),
    retry: false,
    staleTime: 60_000,
  });

  if (!data || data.body === local) return null;
  return (
    <p className="error">
      The server has a newer version of this text — reload before you paste it.
    </p>
  );
}
